<template>
  <div class="forum">
    <v-container>
      <div class="d-flex align-center mb-5">
        <h1>Clients</h1>
        <v-text-field 
          label="Search (use ',' for several conditions)" 
          outlined 
          hide-details 
          v-model="search"
          class="mx-5" 
        ></v-text-field>
        <v-btn
          :loading="loading" 
          @click="syncBillingThisMonth" 
          color="primary"
        >
          Update billing
        </v-btn>
      </div>

      <v-row class="d-flex align-center mb-5">
        <v-col cols="3">
          <v-text-field 
            label="Name" 
            outlined 
            hide-details 
            v-model="add.name"
          ></v-text-field>
        </v-col>
        <v-col cols="3">
          <v-text-field 
            label="Email" 
            outlined 
            hide-details 
            v-model="add.email"
          ></v-text-field>
        </v-col>
        <v-col cols="3">
          <v-autocomplete
            :items="$store.getters['on-load/data']('hs-projects')"
            item-text="name"
            item-value="id"
            outlined
            label="Projects"
            multiple
            hide-details
            v-model="add.hubstaff_project_ids"
          ></v-autocomplete>
        </v-col>
        <v-col cols="2">
          <v-select
            :items="(new Array(28)).fill(0).map((n, i) => i + 1)"
            hide-details
            outlined
            label="Invoicing date"
            v-model="add.invoicing_day"
          ></v-select>
        </v-col>
        <v-col cols="1">
          <v-btn 
            :loading="loading" 
            @click="create" 
            icon 
            color="accent"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </v-col>
      </v-row>

      <pre class="d-flex black align-self-start py-2 px-4 my-2 rounded">
        <div class="d-flex">
          <span>Revenue this month: </span>
          <b>${{ data ? data.reduce((acc, n) => acc + (n.bills[0].amount  - n.bills[0].pay_to_developers), 0).toFixed(2) : '' }}</b>
        </div>
        <v-divider vertical class="mx-5"></v-divider>
        <div class="d-flex">
          <span>Total circulation: </span>
          <b>${{ data ? data.reduce((acc, n) => acc + n.bills[0].amount, 0).toFixed(2) : '' }}</b>
        </div>
        <v-divider vertical class="mx-5"></v-divider>
        <div class="d-flex">
          <span>Tax amount: </span>
          <b>${{ data ? data.reduce((acc, n) => acc + (n.bills[0].amount * 0.05), 0).toFixed(2) : '' }}</b>
        </div>
      </pre>

      <v-data-table
        :headers="headers"
        :items="filteredData"
        class="elevation-1"
        :loading="loading"
        :items-per-page="-1"
      >
        <!-- <template v-slot:item.is_paid="{ item }">
          <v-btn color="warning" icon @click="toggleIsPaid(item)">
            <v-icon
              color="error"
              small
            >
              mdi-currency-usd
            </v-icon>
          </v-btn>
        </template> -->
        <template v-slot:item.name="{ item }">
          <v-text-field hide-details v-model="item.name" label="Name"></v-text-field>
        </template>
        <template v-slot:item.email="{ item }">
          <v-text-field hide-details v-model="item.email" label="Email"></v-text-field>
        </template>
        <template v-slot:item.payment_method="{ item }">
          <v-select
            v-model="item.payment_method" 
            hide-details
            :items="[ 'loz-aval-swift', 'loz-aval-ua', 'b2d-chase-international', 'b2d-chase-usa' ]"
            label="PM"
          ></v-select>
        </template>
        <template v-slot:item.location="{ item }">
          <div class="d-flex flex-column">
            <v-text-field hide-details v-for="(n, i) in item.location" v-model="item.location[i]" label="Line"></v-text-field>
            <v-btn class="mt-2" @click="item.location.push('')">Add</v-btn>
          </div>
        </template>
        <template v-slot:item.bill_2="{ item }">
          <template v-if="item.bills[0]">
            <div class="d-flex flex-column align-start">
              <span class="my-3 font-weight-bold">
                {{ format(item.bills[0].date) }} - ${{ Number(item.bills[0].amount).toFixed(2) }}
              </span>
              <v-btn class="mb-3" :loading="loading" @click="generateInvoice(item.bills[0])" small>
                Generate invoice
              </v-btn>
              <v-btn 
                class="mb-3" 
                :loading="loading" 
                v-if="item.bills[0].invoice" 
                @click="sendInvoice(item.bills[0])" 
                small
                :color="item.bills[0].invoiced ? 'success' : 'error'"
                disabled
              >
                Send invoice
              </v-btn>
              <v-btn target="_blank" class="mb-3" :href="item.bills[0].invoice.pdf_url" v-if="item.bills[0].invoice" small>
                See invoice
              </v-btn>
            </div>
          </template>
          <template v-else>
            -
          </template>
        </template>
        <template v-slot:item.bill_1="{ item }">
          <template v-if="item.bills[1]">
            <div class="d-flex flex-column align-start">
              <span class="my-3 font-weight-bold">
                {{ format(item.bills[1].date) }} - ${{ Number(item.bills[1].amount).toFixed(2) }}
              </span>
              <v-btn class="mb-3" :loading="loading" @click="generateInvoice(item.bills[1])" small>
                Generate invoice
              </v-btn>
              <v-btn 
                class="mb-3" 
                :loading="loading" 
                v-if="item.bills[1].invoice" 
                @click="sendInvoice(item.bills[1])" 
                small
                :color="item.bills[1].invoiced ? 'success' : 'error'"
              >
                Send invoice
              </v-btn>
              <v-btn target="_blank" class="mb-3" :href="item.bills[1].invoice.pdf_url" v-if="item.bills[1].invoice" small>
                See invoice
              </v-btn>
            </div>
          </template>
          <template v-else>
            -
          </template>
        </template>
        <template v-slot:item.techs="{ item }">
          <v-card v-if="item.techs.filter(n => n.core).length" flat class="d-flex pa-2 ma-2 black flex-column align-start">
            <v-chip 
              small 
              color="accent" 
              class="black--text font-weight-bold mb-1" 
              v-for="n in item.techs.filter(n => n.core).map(n => n.name)"
            >
              {{ n }}
            </v-chip>
          </v-card>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn color="error" icon @click="remove(item)">
            <v-icon
              color="error"
              small
            >
              mdi-delete
            </v-icon>
          </v-btn>
          <v-btn color="error" icon @click="update(item)">
            <v-icon
              color="primary"
              small
            >
              mdi-check
            </v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-container>

  </div>
</template>

<script>
import { ClientsAPI } from '@/api';
import { mapGetters } from 'vuex'
import Moment from 'moment'

export default {
  data() {
    return {
      loading: false,
      search: '',
      headers: [
        { text: 'Name', value: 'name' },
        { text: 'Email', value: 'email' },
        { text: 'PM', value: 'payment_method' },
        { text: 'Client data', value: 'location' },
        { text: 'Invoicing date', value: 'invoicing_day' },
        { text: 'Billing (1)', value: 'bill_1' },
        { text: 'Billing (2)', value: 'bill_2' },
        { text: 'Actions', value: 'actions' },
      ],
      add: {
        name: '',
        email: '',
        hubstaff_project_ids: [],
        invoicing_day: 1,
      }
    }
  },
  methods: {
    sync() {
      this.loading = true;
      return this.$store.dispatch('clients/updateData')
        .finally(() => {
          this.loading = false;
        })
    },

    create() {
      this.loading = true;
      return ClientsAPI.create(this.add)
        .then(() => this.sync())
        .finally(() => {
          this.loading = false;
        })
    },

    remove(item) {
      this.loading = true;
      return ClientsAPI.remove(item._id)
        .then(() => this.sync())
        .finally(() => {
          this.loading = false;
        })
    },

    update(item) {
      this.loading = true;
      return ClientsAPI.update(item._id, item)
        .then(() => this.sync())
        .finally(() => {
          this.loading = false;
        })
    },

    generateInvoice(mbill) {
      this.loading = true;
      return ClientsAPI.generateInvoice(mbill._id)
        .then(() => this.sync())
        .finally(() => {
          this.loading = false;
        })
    },

    sendInvoice(mbill) {
      this.loading = true;
      return ClientsAPI.sendInvoice(mbill._id)
        .then(() => this.sync())
        .finally(() => {
          this.loading = false;
        })
    },

    toggleIsPaid(item) {

      if(!item.bills[1]) {
        return;
      }

      this.loading = true;
      return ClientsAPI.setIsPaid(item._id, !item.bills[1].is_paid)
        .then(() => this.sync())
        .finally(() => {
          this.loading = false;
        })
    },

    syncBillingThisMonth() {
      this.loading = true;
      return ClientsAPI.syncBillingThisMonth()
        .then(() => this.sync())
        .finally(() => {
          this.loading = false;
        })
    },
    
    edit(item) {
      this.$store.commit('clients/setForEdit', item)
      this.$store.commit('clients/openModal')
    },

    format(str) {
      return Moment(str).format('MMM')
    },
  },

  created() {
    this.sync();
  },

  computed: {
    ...mapGetters({
      data: 'clients/data',
    }),

    filteredData() {

      const regex = this.search
        .split(',')
        .filter(n => n && n.length)
        .map(n => new RegExp(n.trim(), 'i'))

      return this.data.filter(n => {
        const found_name = regex.every(z => z.test(n.name))
        return found_name;
      })
    }
  },
}
</script>

<style lang="scss" scoped>

.devs {
  
}

</style>